// ─────────────────────────────────────────────────────────────────────────────
// BlogPage, the public journal index at /blog (the footer calls it Journal).
//
// Nothing has been published yet, so this page does not pretend otherwise. It
// is an honest editorial index: the three pieces being written first, the full
// list of subjects the journal will cover, and the rules it will be written
// under. Every row is marked Planned, and no piece carries a publication date,
// a byline or a link to an article that does not exist.
//
// Laid out to the shared marketing kit: left-aligned headings that end in a
// period, hairline borders, oversized card radii, and grey <MediaSlot> panels
// captioned with the photography that belongs there once it is shot.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Body,
    Badge,
    ChipRow,
    MediaSlot,
    SectionHead,
    FeatureRow,
    MediaCardGrid,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
    SITE_URL,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// ── The three pieces being written first ─────────────────────────────────────
const FIRST = [
    {
        title: 'Why the card deposit never matches the day',
        body: 'Batch cutoffs, held funds, refunds netted against sales and fees taken off the top. Four reasons the register total and the bank line will never be the same number, and what to check first when they are further apart than usual.',
        mediaKind: 'Screenshot',
        mediaLabel: 'A day of card sales beside the settlement batch and the bank line',
        mediaNote: 'Three figures, two differences, each one labelled',
        ratio: '4 / 3',
    },
    {
        title: 'What is actually inside a bank statement',
        body: 'A downloaded statement read column by column: posting date against transaction date, the merchant deposit line, the negative entry that is really a refund, and which of those columns a reconciliation can safely rely on.',
        mediaKind: 'Screenshot',
        mediaLabel: 'A statement CSV open beside the imported rows',
        mediaNote: 'Raw file on the left, parsed ledger on the right',
        ratio: '4 / 3',
    },
    {
        title: 'How to run a blind count across several stores',
        body: 'The count sheet, the order you count in, who is allowed to see the expected figure and who is not, and the reason codes that make a short drawer worth reading a week later.',
        mediaKind: 'Photo',
        mediaLabel: 'A manager counting a drawer at the safe after close',
        mediaNote: 'Hands, notes and an iPad, low light, no faces',
        ratio: '4 / 3',
    },
];

// ── The full index. Every one of these is planned, none is published ──────────
const INDEX = [
    {
        area: 'Reconciliation',
        title: 'Why the card deposit never matches the day',
        body: 'Batch cutoffs, held funds, refund netting and fees, and where each one lands.',
    },
    {
        area: 'Reconciliation',
        title: 'What is actually inside a bank statement',
        body: 'Column by column through a downloaded statement, and which columns you can trust.',
    },
    {
        area: 'Cash',
        title: 'How to run a blind count across several stores',
        body: 'The sheet, the sequence, the reason codes and who may see the expected figure.',
    },
    {
        area: 'Cash',
        title: 'Reading an over and short report without blaming the wrong person',
        body: 'Why variance belongs to a drawer, a shift and a name rather than to a store total.',
    },
    {
        area: 'Daily close',
        title: 'What it means for a day to be locked',
        body: 'The case for sign-off that holds, and for changes that have to be made in the open.',
    },
    {
        area: 'Exceptions',
        title: 'What belongs in an exceptions queue, and what does not',
        body: 'A worklist is only useful while it is short. How to decide what earns a place on it.',
    },
    {
        area: 'Wholesale',
        title: 'Why an invoice should be credited, never deleted',
        body: 'Deleting a document removes the evidence. A credit memo leaves the argument on the record.',
    },
    {
        area: 'Reporting',
        title: 'What catch weight does to a report',
        body: 'When a unit is a pound rather than a piece, every average in the report changes meaning.',
    },
    {
        area: 'Inventory',
        title: 'On hand is a consequence, not a field',
        body: 'What changes when stock is derived from an append-only ledger instead of typed over.',
    },
    {
        area: 'Receiving',
        title: 'The back door is a control point',
        body: 'Temperature, count and price checked at the moment of delivery, while it can still be refused.',
    },
    {
        area: 'Accounting',
        title: 'What a daily journal should contain before it reaches a bookkeeper',
        body: 'Tax kept separate, tenders split out, and one entry per location per day.',
    },
    {
        area: 'Analytics',
        title: 'Matching campaign traffic to orders that actually happened',
        body: 'Sessions are not revenue. Joining traffic to real orders, and what that join cannot tell you.',
    },
];

const AREAS = ['Reconciliation', 'Cash', 'Daily close', 'Exceptions', 'Wholesale', 'Reporting', 'Inventory', 'Receiving', 'Accounting', 'Analytics'];

export default function BlogPage() {
    return (
        <MarketingShell>
            <Seo
                title="Journal: reconciliation, cash control and the daily close"
                description="The Wavelength journal. Planned writing on why card deposits never match the day, what a bank statement holds, blind counts, credit memos and catch weight."
                path="/blog"
                keywords="retail reconciliation writing, card settlement versus bank deposit, bank statement import, blind cash count, over and short report, daily close, catch weight reporting, credit memo, exceptions queue"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'CollectionPage',
                            '@id': `${SITE_URL}/blog`,
                            url: `${SITE_URL}/blog`,
                            name: 'Wavelength Journal',
                            description: 'Planned writing on reconciliation, cash accountability and the daily close for multi-location retail and food operators. No articles have been published yet.',
                            publisher: { '@id': `${SITE_URL}/#organization` },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/overview` },
                                { '@type': 'ListItem', position: 2, name: 'Journal', item: `${SITE_URL}/blog` },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Journal"
                title="Field notes from the back office."
                lead="Nothing is published here yet. This is the index of what is being written: one subject per piece, on the parts of running several locations that nobody hands you a manual for."
                primary={{ label: 'Suggest a subject', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <ChipRow items={AREAS} />
            </PageHero>

            {/* ── The first three pieces ─────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="The first three pieces."
                            lead="These are the ones being drafted now. Each is written by the people who built the feature it describes, with worked examples rather than customer stories."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <MediaCardGrid
                            className="mt-12"
                            numbered
                            items={FIRST.map((p) => ({ ...p, footer: <div className="pt-1"><Badge>Planned</Badge></div> }))}
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── The full index ─────────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Everything on the list."
                            lead="The full run of subjects, in the order they are planned. Titles will move as they are written."
                            actions={<Link to="/contact" className={BTN_SECONDARY}>Ask for one first<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <ol className="mt-12 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white">
                            {INDEX.map((p, i) => (
                                <li
                                    key={p.title}
                                    className={`flex flex-col gap-3 px-6 py-6 sm:flex-row sm:items-start sm:gap-8 sm:px-8 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)]' : ''}`}
                                >
                                    <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3] sm:w-10 sm:flex-none sm:pt-1">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <div className="max-w-[600px] flex-1">
                                        <div className="text-[13px] font-medium text-[#A1A1A1]">{p.area}</div>
                                        <h3 className="mt-1.5 text-[17px] font-semibold tracking-[-0.01em] text-[#0A0A0A]">{p.title}</h3>
                                        <Body className="mt-1.5">{p.body}</Body>
                                    </div>
                                    <div className="flex-none sm:pt-1">
                                        <Badge>Planned</Badge>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </Reveal>
                </Container>
            </Section>

            {/* ── How the journal is written ─────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="Editorial"
                            title="How the journal is written."
                            body="A short list of rules, kept for the same reason the product keeps an audit log: so you can tell what you are reading and where it came from."
                            bullets={[
                                'One subject per piece, written by the people who built that part of the product',
                                'Worked examples use invented figures, and say so on the page',
                                'No customer names, no case studies and no figures lifted out of a real set of books',
                                'Corrections are appended underneath, never edited quietly into the original',
                            ]}
                            cta={<Link to="/contact" className={BTN_SECONDARY}>Tell us what to write first<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Photo"
                                    label="A back office desk after close"
                                    note="Count sheets, a statement printout and a laptop, shot from above on a neutral surface"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Journal"
                title="Tell us what to write first."
                body="Say which of these would be worth reading, and it moves to the top of the list. A person answers, and the piece is written by whoever built that part of the product."
                primary={{ label: 'Suggest a subject', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            />
        </MarketingShell>
    );
}
